import { useState, useCallback } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { DbQueryRequest, DbQueryResponse } from './db-explorer.types';

interface UseDbQueryResult {
  loading: boolean;
  error: string | null;
  data: DbQueryResponse | null;
  execute: (request: DbQueryRequest) => Promise<void>;
}

export function useDbQuery(): UseDbQueryResult {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DbQueryResponse | null>(null);

  const execute = useCallback(
    async (request: DbQueryRequest) => {
      setLoading(true);
      setError(null);
      setData(null);

      try {
        const baseUrl = await discoveryApi.getBaseUrl('db-explorer');
        const response = await fetchApi.fetch(`${baseUrl}/query`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
        });

        const json = await response.json();

        if (!response.ok) {
          setError(json.error ?? `Request failed with status ${response.status}`);
          return;
        }

        setData(json as DbQueryResponse);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    },
    [discoveryApi, fetchApi],
  );

  return { loading, error, data, execute };
}
